import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Calendar, Clock, MapPin, Users, Zap } from "lucide-react";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { formatPrice } from "@/utils/formatPrice";

// Matches get-public-landing edge function response shape for open_match
interface OpenMatchEntity {
  start_time: string;
  end_time: string;
  max_players: number;
  duration_minutes: number;
  price_per_hour: number | null;
  info_text: string | null;
  court: { name: string; court_type: string; sport_type: string | null } | null;
  players: Array<{
    full_name: string;
    avatar_url: string | null;
    status: string;
  }>;
  player_count: number;
  spots_left: number;
}

interface Props {
  entity: Record<string, unknown>;
  onAction: (path: string) => void;
  brandColor?: string;
}

export default function OpenMatchLanding({ entity, onAction, brandColor }: Props) {
  const match = entity as unknown as OpenMatchEntity;
  if (!match.start_time) return null;
  const startDate = parseISO(match.start_time);
  const endDate = parseISO(match.end_time);
  const maxPlayers = match.max_players ?? 4;
  const spotsLeft = match.spots_left ?? 0;
  const players = match.players ?? [];
  const pricePerPlayer = match.price_per_hour
    ? Math.round((match.price_per_hour * (match.duration_minutes / 60)) / maxPlayers)
    : null;

  const levelRange = match.info_text;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold">
          Öppen match
        </h1>
        {levelRange && (
          <Badge variant="secondary" className="gap-1">
            <Zap className="h-3 w-3" />
            {levelRange}
          </Badge>
        )}
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span>{format(startDate, "EEEE d MMMM yyyy", { locale: sv })}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>
              {format(startDate, "HH:mm")}–{format(endDate, "HH:mm")}
            </span>
          </div>
          {match.court && (
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>{match.court.name}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span>
              {match.player_count}/{maxPlayers} spelare
              {spotsLeft > 0 && (
                <span className="text-muted-foreground"> ({spotsLeft} platser kvar)</span>
              )}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Players */}
      {players.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm font-medium mb-3">Spelare</p>
            <div className="flex flex-wrap gap-2">
              {players.map((p, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-muted rounded-full px-3 py-1">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={p.avatar_url ?? undefined} />
                    <AvatarFallback className="text-xs">
                      {p.full_name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm">{p.full_name}</span>
                </div>
              ))}
              {/* Empty spots */}
              {Array.from({ length: spotsLeft }, (_, i) => (
                <div key={`empty-${i}`} className="flex items-center gap-2 bg-muted/50 border border-dashed rounded-full px-3 py-1">
                  <div className="h-6 w-6 rounded-full border-2 border-dashed border-muted-foreground/30" />
                  <span className="text-sm text-muted-foreground">Ledig plats</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Price */}
      {pricePerPlayer != null && pricePerPlayer > 0 && (
        <div className="text-center text-sm text-muted-foreground">
          {formatPrice(pricePerPlayer)} / spelare
        </div>
      )}

      {/* Action */}
      <Button
        className="w-full text-base py-6"
        style={brandColor ? { backgroundColor: brandColor } : undefined}
        disabled={spotsLeft <= 0}
        onClick={() => onAction(`/play`)}
      >
        {spotsLeft <= 0 ? "Matchen är full" : "Gå med i matchen →"}
      </Button>
    </div>
  );
}
